import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Components } from '@one/web-components';
import { EnclaveResponse } from 'src/app/shared/models/enclave';
import { AccessService, RolePermissionResponse, TermsAndConditionResponse } from 'src/app/shared/services/access.service';
import { EnclaveService } from 'src/app/shared/services/enclave.service';
import { environment as env } from 'src/environments/environment';
import { Dialog } from '@angular/cdk/dialog';
import { TermsPageComponent } from '../terms-page/terms-page.component';
import { NavifyService } from 'src/app/auth/services/navify.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
interface SuccessfulResponse{
  [key:string]:any;
}
interface ErrorResponse<T> {
  [key:string]:T
}
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
 
  @ViewChild('drawerElement', { static: true })
  drawerElement!: Components.OwcDrawer;
  isLoading: boolean = false;
  visible: boolean = true;
  isAccepted:boolean = false;
  private tcCheckTimeout?: ReturnType<typeof setTimeout>;
 
  constructor(
    protected readonly enclaveService: EnclaveService,
    private readonly router: Router,
    protected accessService: AccessService,
    private readonly dialog: Dialog,
    private readonly navifyService: NavifyService,
  ) { }
 
  ngOnInit() {
    // OAuth2 is handled by route guard before component loads
    // Check if OAuth2 exchange is done (completed OR failed) before loading APIs
    const exchangeCompleted = sessionStorage.getItem('oauth2_exchange_completed');
    const exchangeFailed = sessionStorage.getItem('oauth2_exchange_failed');
    
    // Proceed if exchange is completed OR failed (OR condition)
    // if (exchangeCompleted || exchangeFailed) {
    //   console.info('[HomeComponent] OAuth2 exchange done — loading APIs');
    // } else {
    //   // Neither completed nor failed - OAuth2 exchange still in progress
    //   // this.isLoading = false;
    //   // return;
    // }
    
    // Fire-and-forget: Log user entry for audit (non-blocking)
    this.enclaveService.updateUser().subscribe({
      error: (error) => console.error('[HomeComponent] Error logging user entry:', error)
    });
    
    this.loadInitialDataParallel();
  }

  ngOnDestroy() {
    // Clear pending timeout to prevent execution after component destruction
    if (this.tcCheckTimeout) {
      clearTimeout(this.tcCheckTimeout);
    }
  }

  /**
   * Load all initial data in parallel for faster page load
   * Performance improvement: reduces sequential waterfall from 2-4s to <1s
   * Note: Endor login check runs in background to not block page load
   */
  private loadInitialDataParallel() {
    this.isLoading = true;
    
    // Set default for endor validity (will be updated asynchronously)
    this.enclaveService.endorCheckValidity = false;
    
    // Load endor validity in background (non-blocking)
    this.loadEndorValidityInBackground();
    
    // Execute only critical API calls in parallel using forkJoin
    // Note: updateUser() and endorLogin() are called separately (non-blocking)
    forkJoin({
      enclave: this.enclaveService.getEnclaveDetails().pipe(
        catchError(error => {
          console.error('Error loading enclave details:', error);
          return of({
            status: 'ERROR',
            responseCode: '500',
            messages: ['Error loading enclave details'],
            message: '',
            error: [],
            result: null as any
          } as EnclaveResponse);
        })
      ),
      permissions: this.accessService.getRolePermission().pipe(
        catchError(error => {
          console.error('Error loading permissions:', error);
          return of({
            status: 'ERROR',
            responseCode: '500',
            messages: ['Error loading permissions'],
            message: '',
            error: [],
            result: null as any
          } as RolePermissionResponse);
        })
      )
    }).subscribe({
      next: (results) => {
        // Process enclave details
        if (results.enclave.result) {
          this.enclaveService.enclaveDetails = results.enclave.result;
        }
        
        // Process permissions
        if (results.permissions.result) {
          this.accessService.permissionDetails = results.permissions.result;
        }
        
        // Handle navigation logic
        this.handlePostLoadNavigation(results.enclave.result);
        
        // Load Terms & Conditions in background (non-blocking)
        this.deferredLoadTermsAndConditions();
      },
      error: (error) => {
        console.error('Error loading initial data:', error);
        this.isLoading = false;
      }
    });
  }

  /**
   * Load endor validity check in background without blocking page load
   * Performance improvement: prevents 6+ second endor login from blocking UI
   */
  private loadEndorValidityInBackground() {
    this.enclaveService.endorLogin({ username: null, password: null }).subscribe({
      next: (result) => {
        if (result) {
          this.enclaveService.endorCheckValidity = true;
        } else {
          this.enclaveService.endorCheckValidity = false;
        }
      },
      error: (error) => {
        this.enclaveService.endorCheckValidity = false;
      }
    });
  }

  /**
   * Handle navigation based on enclave configuration
   */
  private handlePostLoadNavigation(enclaveResult: any) {
    if (enclaveResult?.enclaveStatus === env.ENC_CONF.STATUS_CONFIGURED &&
        !this.accessService.permissionDetails?.enclaveAdmin &&
        Object.keys(this.accessService.permissionDetails?.studyUserRole || {}).length === 0) {
      this.router.navigate(['u1']);
      this.isLoading = false;
    } else if (enclaveResult?.enclaveStatus !== env.ENC_CONF.STATUS_CONFIGURED) {
      this.router.navigate(['config']).then(() => {
        this.isLoading = false;
      });
    } else {
      this.isLoading = false;
    }
  }
 
  /**
   * Load Terms & Conditions in background after initial render
   * Performance improvement: defers T&C check to not block rendering
   */
  private deferredLoadTermsAndConditions() {
    // Use setTimeout to defer execution after render
    this.tcCheckTimeout = setTimeout(() => {
      this.getTermsAndConditionDetails();
    }, 100);
  }

  private getTermsAndConditionDetails(){
    this.accessService.getTCDetails().subscribe((data)=>{
      const filteredData = data.result?.filter(values=>values.tcName == 'TermsAndCondition-Login')
      this.isAccepted = filteredData[0]?.accepted
      const expired = filteredData[0]?.acceptanceExpired
      if(data.result.length === 0 || !this.isAccepted || expired){
        const dialogRef = this.dialog.open(TermsPageComponent,
          {
              data: {event:'login'}
          })
        dialogRef.closed.subscribe((res) => {
        if(!res){
          const payload = {accepted:false,tcName:'TermsAndCondition-Login'}
          this.accessService.sendTcDetails(payload).subscribe({
              next: (resp: TermsAndConditionResponse) => {  
                this.navifyService.logOut();         
            },
            error: (_error) => { 
            }
          }) 
        }
        else{
        const payload = {accepted:true,tcName:'TermsAndCondition-Login'}
        this.accessService.sendTcDetails(payload).subscribe({
            next: (resp: TermsAndConditionResponse) => {  
              dialogRef.close() ;         
          },
          error: (_error) => { 
          }
        })  
        }
        });
      }
    })
  }


 
}